import { useState, useEffect } from 'react';
import { Card, Button, Input, Space, Typography, Alert, Tabs } from 'antd';
import { PlayCircleOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { useApiKey } from './ApiKeyContext';
import { useCommonParams } from './CommonParamsContext';
import { API_BASE_URL } from '../../utils/constants';
import type { EndpointInfo } from './EndpointParser';
import './ApiTester.css';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

interface ApiTesterProps {
  endpoint: EndpointInfo;
}

interface ApiResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  data: unknown;
  error?: string;
}

export const ApiTester = ({ endpoint }: ApiTesterProps) => {
  const { apiKey, isConfigured } = useApiKey();
  const { seasonYear, circuitId, startDate, source } = useCommonParams();
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<ApiResponse | null>(null);
  const [pathParams, setPathParams] = useState<Record<string, string>>({});
  const [queryParams, setQueryParams] = useState<Record<string, string>>({});
  const [requestBody, setRequestBody] = useState('{}');
  const [copied, setCopied] = useState(false);

  // Reset and auto-fill params when endpoint or common params change
  useEffect(() => {
    const newPathParams: Record<string, string> = {};
    const newQueryParams: Record<string, string> = {};

    // Helper function to get common param value for a parameter name
    const getCommonParamValue = (paramName: string): string | null => {
      // Remove backticks and normalize
      const cleanName = paramName.replace(/`/g, '').toLowerCase().trim();
      
      // Check for season_year or year
      if ((cleanName === 'season_year' || cleanName === 'year') && seasonYear) {
        return seasonYear;
      }
      // Check for circuit_id
      if (cleanName === 'circuit_id' && circuitId) {
        return circuitId;
      }
      // Check for start_date
      if (cleanName === 'start_date' && startDate) {
        return startDate;
      }
      // Check for source
      if (cleanName === 'source' && source) {
        return source;
      }
      return null;
    };

    // Initialize path parameters with common params
    if (endpoint.pathParams && endpoint.pathParams.length > 0) {
      endpoint.pathParams.forEach((param) => {
        const commonValue = getCommonParamValue(param.name);
        if (commonValue) {
          newPathParams[param.name] = commonValue;
        }
      });
    }

    // Initialize query parameters with common params
    if (endpoint.queryParams && endpoint.queryParams.length > 0) {
      endpoint.queryParams.forEach((param) => {
        const commonValue = getCommonParamValue(param.name);
        if (commonValue) {
          newQueryParams[param.name] = commonValue;
        }
      });
    }

    setPathParams(newPathParams);
    setQueryParams(newQueryParams);
    setResponse(null);
  }, [seasonYear, circuitId, startDate, source, endpoint.path, endpoint.method, endpoint.pathParams, endpoint.queryParams]);

  const buildUrl = (): string => {
    let url = `${API_BASE_URL}${endpoint.path}`;
    
    // Replace path parameters
    if (endpoint.pathParams) {
      endpoint.pathParams.forEach((param) => {
        const value = pathParams[param.name] || '';
        url = url.replace(`:${param.name}`, value);
      });
    }
    
    // Add query parameters
    const queryString = Object.entries(queryParams)
      .filter(([, value]) => value.trim() !== '')
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    
    if (queryString) {
      url += `?${queryString}`;
    }
    
    return url;
  };

  const handleTest = async () => {
    if (!isConfigured || !apiKey) {
      return;
    }

    setLoading(true);
    setResponse(null);
    setCopied(false);

    try {
      const url = buildUrl();
      
      const options: RequestInit = {
        method: endpoint.method,
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
        },
      };

      // Add body for POST/PUT/PATCH
      if (endpoint.hasBody && requestBody) {
        try {
          // Validate JSON
          JSON.parse(requestBody);
          options.body = requestBody;
        } catch {
          setResponse({
            status: 0,
            statusText: 'Error',
            headers: {},
            data: null,
            error: 'Invalid JSON in request body',
          });
          setLoading(false);
          return;
        }
      }

      const fetchResponse = await fetch(url, options);

      // Get response headers
      const headers: Record<string, string> = {};
      fetchResponse.headers.forEach((value, key) => {
        headers[key] = value;
      });

      // Parse response body
      let data: unknown;
      const contentType = fetchResponse.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        data = await fetchResponse.json();
      } else {
        data = await fetchResponse.text();
      }

      setResponse({
        status: fetchResponse.status,
        statusText: fetchResponse.statusText,
        headers,
        data,
      });
    } catch (error) {
      setResponse({
        status: 0,
        statusText: 'Error',
        headers: {},
        data: null,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setLoading(false);
    }
  };

  const copyResponse = () => {
    if (response) {
      const text = JSON.stringify(response.data, null, 2);
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatJson = (obj: unknown): string => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  if (!isConfigured) {
    return (
      <Alert
        title="API Key Required"
        description="Please configure your API key above to test this endpoint."
        type="warning"
        showIcon
        style={{ marginTop: 16 }}
      />
    );
  }

  return (
    <Card className="api-tester-card" title="Try it out">
      <Space orientation="vertical" style={{ width: '100%' }} size="middle">
        {/* Path Parameters */}
        {endpoint.pathParams && endpoint.pathParams.length > 0 && (
          <div>
            <Text strong>Path Parameters</Text>
            <Space orientation="vertical" style={{ width: '100%', marginTop: 8 }} size="small">
              {endpoint.pathParams.map((param) => (
                <div key={param.name}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {param.name} ({param.type})
                    {param.required && <Text type="danger"> *</Text>}
                  </Text>
                  <Input
                    placeholder={param.description || `Enter ${param.name}`}
                    value={pathParams[param.name] || ''}
                    onChange={(e) =>
                      setPathParams({ ...pathParams, [param.name]: e.target.value })
                    }
                    style={{ marginTop: 4 }}
                  />
                </div>
              ))}
            </Space>
          </div>
        )}

        {/* Query Parameters */}
        {endpoint.queryParams && endpoint.queryParams.length > 0 && (
          <div>
            <Text strong>Query Parameters</Text>
            <Space orientation="vertical" style={{ width: '100%', marginTop: 8 }} size="small">
              {endpoint.queryParams.map((param) => (
                <div key={param.name}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {param.name} ({param.type})
                    {param.required && <Text type="danger"> *</Text>}
                  </Text>
                  <Input
                    placeholder={param.description || `Enter ${param.name}`}
                    value={queryParams[param.name] || ''}
                    onChange={(e) =>
                      setQueryParams({ ...queryParams, [param.name]: e.target.value })
                    }
                    style={{ marginTop: 4 }}
                  />
                </div>
              ))}
            </Space>
          </div>
        )}

        {/* Request Body */}
        {endpoint.hasBody && (
          <div>
            <Text strong>Request Body</Text>
            <TextArea
              value={requestBody}
              onChange={(e) => setRequestBody(e.target.value)}
              rows={6}
              placeholder='{"key": "value"}'
              style={{ marginTop: 8, fontFamily: 'monospace' }}
            />
          </div>
        )}

        {/* Request URL Preview */}
        <div>
          <Text strong>Request URL</Text>
          <Paragraph
            copyable={{ text: buildUrl() }}
            style={{
              marginTop: 8,
              padding: 8,
              background: 'var(--ant-color-fill-tertiary)',
              borderRadius: 4,
              fontFamily: 'monospace',
              fontSize: 12,
              wordBreak: 'break-all',
            }}
          >
            {buildUrl()}
          </Paragraph>
        </div>

        {/* Send Button */}
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          onClick={handleTest}
          loading={loading}
          block
        >
          Send Request
        </Button>

        {/* Response */}
        {response && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong>Response</Text>
              <Button
                size="small"
                icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                onClick={copyResponse}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            
            <Tabs
              items={[
                {
                  key: 'body',
                  label: 'Body',
                  children: (
                    <pre className="api-response-body">
                      {response.error
                        ? response.error
                        : formatJson(response.data)}
                    </pre>
                  ),
                },
                {
                  key: 'headers',
                  label: 'Headers',
                  children: (
                    <pre className="api-response-body">
                      {formatJson(response.headers)}
                    </pre>
                  ),
                },
              ]}
            />
            
            <div style={{ marginTop: 8 }}>
              <Text
                type={response.status >= 200 && response.status < 300 ? 'success' : 'danger'}
                strong
              >
                Status: {response.status} {response.statusText}
              </Text>
            </div>
          </div>
        )}
      </Space>
    </Card>
  );
};

